/**
 * cleanCharacterImage — strip stock-image background from a PNG.
 *
 * Pipeline:
 *   1. Sample edge pixels to learn the background palette (1 colour for
 *      flat-white margins, 2 for checkerboard placeholders, n for
 *      gradients).
 *   2. Flood-fill from every edge pixel that matches the palette,
 *      marking the connected background region transparent. This
 *      handles the outer halo cleanly.
 *   3. Global pass for *interior* holes — pixels that match the
 *      palette but were unreachable from the edge (e.g. checkerboard
 *      poking through between Barbie's legs, Stella's hair loops). A
 *      pixel is marked transparent only when ≥ 4 of its 9-pixel
 *      neighbourhood also match the palette, so isolated lookalike
 *      pixels inside the character (sparkles, skin highlights) are
 *      preserved.
 *   4. Edge-feather pass that softens single-pixel halos around the
 *      character so the cutout does not show jaggies on the gradient
 *      background.
 *
 * Returns a `data:image/png` URL ready to feed an `<img>`.
 */

export function cleanCharacterImage(
  imageUrl: string,
  opts: { tolerance?: number; interiorTolerance?: number } = {},
): Promise<string> {
  const { tolerance = 36, interiorTolerance = 24 } = opts;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (ctx === null) {
        resolve(imageUrl);
        return;
      }
      ctx.drawImage(img, 0, 0);

      const w = canvas.width;
      const h = canvas.height;
      const total = w * h;
      const imageData = ctx.getImageData(0, 0, w, h);
      const data = imageData.data;

      // ── 1. Sample edge colours, deduplicated ───────────────────────
      const samples: Array<[number, number, number]> = [];
      const dedup = (
        r: number,
        g: number,
        b: number,
        epsilon: number,
      ): boolean => {
        for (const [sr, sg, sb] of samples) {
          if (
            Math.abs(r - sr) < epsilon &&
            Math.abs(g - sg) < epsilon &&
            Math.abs(b - sb) < epsilon
          ) {
            return true;
          }
        }
        return false;
      };
      const addSample = (x: number, y: number): void => {
        const i = (y * w + x) * 4;
        const a = data[i + 3] ?? 0;
        if (a < 10) return;
        const r = data[i] ?? 0;
        const g = data[i + 1] ?? 0;
        const b = data[i + 2] ?? 0;
        if (dedup(r, g, b, 16)) return;
        samples.push([r, g, b]);
      };
      const stepX = Math.max(1, Math.floor(w / 64));
      const stepY = Math.max(1, Math.floor(h / 64));
      for (let x = 0; x < w; x += stepX) {
        addSample(x, 0);
        addSample(x, h - 1);
      }
      for (let y = 0; y < h; y += stepY) {
        addSample(0, y);
        addSample(w - 1, y);
      }

      const matchesPalette = (
        i: number,
        epsilon: number,
      ): boolean => {
        const a = data[i * 4 + 3] ?? 0;
        if (a < 10) return true;
        const r = data[i * 4] ?? 0;
        const g = data[i * 4 + 1] ?? 0;
        const b = data[i * 4 + 2] ?? 0;
        for (const [sr, sg, sb] of samples) {
          if (
            Math.abs(r - sr) < epsilon &&
            Math.abs(g - sg) < epsilon &&
            Math.abs(b - sb) < epsilon
          ) {
            return true;
          }
        }
        return false;
      };

      // ── 2. Flood-fill from edges ───────────────────────────────────
      const visited = new Uint8Array(total);
      const stack: number[] = [];
      const push = (i: number): void => {
        if (i < 0 || i >= total || visited[i] === 1) return;
        if (!matchesPalette(i, tolerance)) return;
        visited[i] = 1;
        stack.push(i);
      };

      for (let x = 0; x < w; x++) {
        push(x);
        push((h - 1) * w + x);
      }
      for (let y = 0; y < h; y++) {
        push(y * w);
        push(y * w + w - 1);
      }
      while (stack.length > 0) {
        const i = stack.pop() as number;
        data[i * 4 + 3] = 0;
        const x = i % w;
        const y = (i / w) | 0;
        if (x > 0) push(i - 1);
        if (x < w - 1) push(i + 1);
        if (y > 0) push(i - w);
        if (y < h - 1) push(i + w);
      }

      // ── 3. Interior hole pass ─────────────────────────────────────
      //      Any opaque pixel that (a) matches the palette and (b) has
      //      ≥ 4 palette-coloured neighbours in its 3x3 window is
      //      almost certainly leftover background poking through. Real
      //      character pixels with similar hue (Barbie's tutu sparkles,
      //      Stella's skin) won't have that many palette neighbours.
      const toClear: number[] = [];
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = y * w + x;
          if (data[i * 4 + 3] === 0) continue;
          if (!matchesPalette(i, interiorTolerance)) continue;
          let n = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue;
              const nx = x + dx;
              const ny = y + dy;
              if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
              const ni = ny * w + nx;
              if (data[ni * 4 + 3] === 0) {
                n++;
              } else if (matchesPalette(ni, interiorTolerance)) {
                n++;
              }
            }
          }
          if (n >= 4) toClear.push(i);
        }
      }
      for (const i of toClear) data[i * 4 + 3] = 0;

      // ── 4. Edge feather — kill single-pixel halos ─────────────────
      const orig = new Uint8ClampedArray(data);
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          if ((orig[i * 4 + 3] ?? 0) === 0) continue;
          let transparentNeighbors = 0;
          if ((orig[(i - 1) * 4 + 3] ?? 0) === 0) transparentNeighbors++;
          if ((orig[(i + 1) * 4 + 3] ?? 0) === 0) transparentNeighbors++;
          if ((orig[(i - w) * 4 + 3] ?? 0) === 0) transparentNeighbors++;
          if ((orig[(i + w) * 4 + 3] ?? 0) === 0) transparentNeighbors++;
          if (transparentNeighbors >= 3) {
            data[i * 4 + 3] = Math.floor((orig[i * 4 + 3] ?? 255) * 0.4);
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
}
